import { forwardRef, type ReactNode } from "react";
import { useField } from "formik";

import {
  TextareaInput,
  type TextareaInputProps,
} from "@vega-core/components/Form/Controls/Textarea/TextareaInput";
import { useTouchFieldOnSubmit } from "@vega-core/helpers/formik.helper";
import { isTruthy } from "@vega-core/utils/isTruthy";

type Props = Omit<TextareaInputProps, "onChange" | "value"> & {
  /**
   * @deprecated Use `isOptional` instead to align with the underlying props name
   */
  isRequired?: boolean;
  isOptional?: boolean;
  label?: string;
  labelTag?: ReactNode;
  name: string;
  compact?: boolean;
  fullWidth?: boolean;
  constrainInputWidth?: boolean;
};

export const TextareaField = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      label,
      labelTag,
      name,
      compact,
      fullWidth,
      isOptional = false,
      constrainInputWidth = false,
      ...props
    },
    ref,
  ) => {
    const [field, meta] = useField<string>(name);
    useTouchFieldOnSubmit(name);

    return (
      <TextareaInput
        {...props}
        compact={compact}
        constrainInputWidth={constrainInputWidth}
        error={meta.touched && isTruthy(meta.error) ? meta.error : undefined}
        fullWidth={fullWidth}
        isOptional={isOptional}
        label={label}
        labelTag={labelTag}
        name={field.name}
        onBlur={field.onBlur}
        onChange={field.onChange}
        ref={ref}
        value={field.value}
        withBorder
      />
    );
  },
);

TextareaField.displayName = "TextareaField";
